/**
 * channelReorderPlan — pure helpers for the "reorderPlan per channel" model
 * introduced to fix the 2026-05-21 v2 user bug ("source drop on occupied slot
 * still replaces") and to enable existing-slot reorder inside the AddToProduct
 * overlay (mirrors `tab=assets` DnD semantics).
 *
 * A ChannelPlan is the FULL final slot order for one channel after all pending
 * mutations have been applied. Index 0 = slot 1. Existing slot images keep
 * their `linkId`; new (pending) drops carry a `tempKey` so the save pipeline
 * can resolve them to the eventually-created link_id.
 *
 * Plans are derived from the TanStack QueryClient cache (no `snapshot` prop
 * drilling, no race with the occupancy refresh).
 */
import { arrayMove } from '@dnd-kit/sortable'
import type { QueryClient } from '@tanstack/react-query'
import { getAssetUrl } from '@/lib/helpers/asset-urls'
import type { ChannelSlot } from '@/lib/api/products'
import type { AddToProductImage } from '../types'

export type ChannelPlanItem =
  | { kind: 'existing'; linkId: string; image: AddToProductImage }
  | { kind: 'new'; tempKey: string; image: AddToProductImage }

export type ChannelReorderPlans = Map<string, ChannelPlanItem[]>

/**
 * Reads the current Save-state for a channel synchronously from the TanStack
 * cache. Returns the existing slot images in slot ascending order so the plan
 * can be initialised in a single pass.
 */
export function getChannelSnapshotFromCache(
  qc: QueryClient,
  productId: string,
  channelId: string,
): ChannelPlanItem[] {
  const slots = qc.getQueryData<ChannelSlot[]>([
    'product-channel-assets',
    productId,
    channelId,
  ])
  const list = Array.isArray(slots) ? slots : []
  return list
    .filter((s) => !s.empty && !!s.asset_id && !!s.id)
    .slice()
    .sort((a, b) => a.slot - b.slot)
    .map((s) => {
      const assetId = s.asset_id as string
      const linkId = s.id as string
      return {
        kind: 'existing' as const,
        linkId,
        image: {
          id: assetId,
          url: getAssetUrl(assetId, 'thumb'),
          kind: 'asset' as const,
        },
      } satisfies ChannelPlanItem
    })
}

/** Insert a NEW pending item at the user's target slot (1-based). */
export function applyInsertToPlan(
  plan: ChannelPlanItem[],
  targetSlot: number,
  image: AddToProductImage,
  tempKey: string,
): ChannelPlanItem[] {
  const idx = Math.max(0, Math.min(plan.length, targetSlot - 1))
  const next = plan.slice()
  next.splice(idx, 0, { kind: 'new', tempKey, image })
  return next
}

/**
 * Move the plan item currently at fromSlot (1-based) to toSlot (1-based).
 * Out-of-range slots fall back to the closest valid index. No-op when
 * fromSlot === toSlot or the plan is too short.
 */
export function applyReorderToPlan(
  plan: ChannelPlanItem[],
  fromSlot: number,
  toSlot: number,
): ChannelPlanItem[] {
  if (plan.length < 2) return plan
  const fromIdx = Math.max(0, Math.min(plan.length - 1, fromSlot - 1))
  const toIdx = Math.max(0, Math.min(plan.length - 1, toSlot - 1))
  if (fromIdx === toIdx) return plan
  return arrayMove(plan, fromIdx, toIdx)
}

/** Remove the item at slot (1-based). Returns the same array if out-of-range. */
export function applyDeleteToPlan(
  plan: ChannelPlanItem[],
  slot: number,
): ChannelPlanItem[] {
  const idx = slot - 1
  if (idx < 0 || idx >= plan.length) return plan
  const next = plan.slice()
  next.splice(idx, 1)
  return next
}

/** True if the plan deviates from the initial existing-only snapshot. */
export function isPlanDirty(plan: ChannelPlanItem[]): boolean {
  return plan.some((it) => it.kind === 'new')
}
